/**
 * StatusPill — colour-coded job-status chip (WSRPDM-0011).
 *
 * Presentational: takes the raw status string, resolves its label through
 * the registry (`statusLabel`, raw-enum fallback for anything unmapped),
 * and picks a colour bucket. SUCCEEDED → green, FAILED/ERROR → red,
 * RUNNING/CLAIMED → blue, PENDING → neutral, SKIPPED → muted; an unmapped
 * status still renders (neutral/outlined) rather than breaking.
 */
import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { statusLabel } from '@/domain/registry';

type ChipColor = 'default' | 'success' | 'error' | 'info';
type ChipVariant = 'filled' | 'outlined';

interface StatusStyle {
  color: ChipColor;
  variant: ChipVariant;
  sx?: SxProps<Theme>;
}

const STATUS_STYLE: Record<string, StatusStyle> = {
  SUCCEEDED: { color: 'success', variant: 'filled' },
  FAILED: { color: 'error', variant: 'filled' },
  ERROR: { color: 'error', variant: 'filled' },
  RUNNING: { color: 'info', variant: 'filled' },
  CLAIMED: { color: 'info', variant: 'filled' },
  PENDING: { color: 'default', variant: 'outlined' },
  SKIPPED: { color: 'default', variant: 'filled', sx: { opacity: 0.65 } },
};

const DEFAULT_STYLE: StatusStyle = { color: 'default', variant: 'outlined' };

interface StatusPillProps {
  status: string;
}

export function StatusPill({ status }: StatusPillProps) {
  const { t } = useCopy();
  const label = statusLabel(status, t);
  const style = STATUS_STYLE[status] ?? DEFAULT_STYLE;
  return (
    <Chip label={label} color={style.color} variant={style.variant} size="small" sx={style.sx} />
  );
}
